import { AxiosResponse } from 'axios';
import FileDownload from 'js-file-download';

import { AxiosAccessTokenClientBearer } from '@hcs/http-clients';
import {
  AccroleapiInputKeyRequest,
  AccroleapiRolesResp,
  OrganizationapiBulkInviteUploadTaskStatusResponse,
  OrganizationapiCreateOrgInviteRequest,
  OrganizationapiDeleteInviteReq,
  OrganizationapiInviteListResponse,
  OrganizationapiInviteResponse,
  OrganizationapiOrgUserListResponse,
  OrganizationapiResendOrgInviteRequest,
  OrganizationapiUploadOrgInvitesResponse,
  Role,
} from '@hcs/types';
import {
  ACCOUNT_URL,
  ACCOUNT_URL_EXTERNAL,
  ACCOUNT_URL_INTERNAL,
} from '@hcs/urls';
import { getDateStr } from '@hcs/utils';

const getRolesEndpoint = (orgId: number, userId: number) =>
  ACCOUNT_URL === ACCOUNT_URL_INTERNAL
    ? `${ACCOUNT_URL_INTERNAL}/users/${userId}/roles`
    : `${ACCOUNT_URL}/organizations/${orgId}/users/${userId}/roles`;

export const OrgUsersApi = {
  getOrgInvites: async (orgId: number) => {
    const response =
      await AxiosAccessTokenClientBearer.get<OrganizationapiInviteListResponse>(
        `${ACCOUNT_URL}/organizations/${orgId}/invites`,
      );
    return response.data;
  },
  createOrgInvite: async (
    orgId: number,
    inviteData: OrganizationapiCreateOrgInviteRequest,
    acceptInvitationUrl?: string,
  ) => {
    const response = await AxiosAccessTokenClientBearer.post<
      OrganizationapiInviteResponse,
      AxiosResponse<OrganizationapiInviteResponse>,
      OrganizationapiCreateOrgInviteRequest
    >(
      `${ACCOUNT_URL}/organizations/${orgId}/invites${acceptInvitationUrl ? `?url=${acceptInvitationUrl}` : ''}`,
      inviteData,
    );
    return response.data;
  },
  resendOrgInvite: async (
    orgId: number,
    payload: OrganizationapiResendOrgInviteRequest,
    acceptInvitationUrl?: string,
  ) => {
    const response = await AxiosAccessTokenClientBearer.post<
      void,
      AxiosResponse<void>,
      OrganizationapiResendOrgInviteRequest
    >(
      `${ACCOUNT_URL}/organizations/${orgId}/invites/resend${acceptInvitationUrl ? `?url=${acceptInvitationUrl}` : ''}`,
      payload,
    );
    return response.data;
  },
  createBulkInvitation: async (
    orgId: number,
    payload: FormData,
    acceptInvitationUrl?: string,
  ) => {
    const response =
      await AxiosAccessTokenClientBearer.post<OrganizationapiUploadOrgInvitesResponse>(
        `${ACCOUNT_URL}/organizations/${orgId}/invites/bulk${acceptInvitationUrl ? `?url=${acceptInvitationUrl}` : ''}`,
        payload,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      );
    return response.data;
  },
  getBulkInvitationTaskStatus: async (orgId: number, taskId: string) => {
    const response =
      await AxiosAccessTokenClientBearer.get<OrganizationapiBulkInviteUploadTaskStatusResponse>(
        `${ACCOUNT_URL}/organizations/${orgId}/invites/bulk/${taskId}`,
      );
    return response.data;
  },
  deleteOrgInvite: async (
    orgId: number,
    inviteEmailData: OrganizationapiDeleteInviteReq,
  ) => {
    await AxiosAccessTokenClientBearer.delete<
      void,
      AxiosResponse<void>,
      OrganizationapiDeleteInviteReq
    >(`${ACCOUNT_URL}/organizations/${orgId}/invites`, {
      data: inviteEmailData,
    });
  },
  getOrgUsers: async (orgId: number) => {
    const response =
      await AxiosAccessTokenClientBearer.get<OrganizationapiOrgUserListResponse>(
        `${ACCOUNT_URL}/organizations/${orgId}/users`,
      );
    return response.data;
  },
  exportOrgUsers: async (orgId: number) => {
    const response = await AxiosAccessTokenClientBearer.get<Blob>(
      `${ACCOUNT_URL_EXTERNAL}/organizations/${orgId}/download-users`,
      { responseType: 'blob' },
    );
    const dateString = getDateStr(new Date());
    FileDownload(response.data, `users_${dateString}.csv`);
  },
  deleteOrgUser: async (orgId: number, userId: number) => {
    await AxiosAccessTokenClientBearer.delete(
      `${ACCOUNT_URL}/organizations/${orgId}/users/${userId}`,
    );
  },
  getUserRoles: async (orgId: number, userId: number) => {
    const response =
      await AxiosAccessTokenClientBearer.get<AccroleapiRolesResp>(
        getRolesEndpoint(orgId, userId),
      );
    return response.data;
  },
  addUserRole: async (orgId: number, userId: number, roleKey: Role['key']) => {
    return await AxiosAccessTokenClientBearer.post<
      void,
      AxiosResponse<void>,
      AccroleapiInputKeyRequest
    >(getRolesEndpoint(orgId, userId), {
      key: roleKey,
    });
  },
  removeUserRole: async (
    orgId: number,
    userId: number,
    roleKey: Role['key'],
  ) => {
    return await AxiosAccessTokenClientBearer.delete<
      void,
      AxiosResponse<void>,
      AccroleapiInputKeyRequest
    >(getRolesEndpoint(orgId, userId), {
      data: { key: roleKey },
    });
  },
  getOrgRoles: async (orgId: number) => {
    const response =
      await AxiosAccessTokenClientBearer.get<AccroleapiRolesResp>(
        `${ACCOUNT_URL}/organizations/${orgId}/roles`,
      );
    return response.data;
  },
};
